/**
 * Application router — full route tree for KozéCho web app.
 *
 * Route structure:
 *
 *   /                          → redirect to /app
 *   /login                     → GuestGuard → LoginScreen
 *   /signup                    → GuestGuard → SignupScreen
 *   /forgot-password           → GuestGuard → ForgotPasswordScreen
 *   /reset-code                → GuestGuard → ResetCodeScreen
 *   /new-password              → GuestGuard → NewPasswordScreen
 *   /app                       → AuthGuard → AppLayout
 *     /app/                    → FeedScreen (index)
 *     /app/kozecho             → KozeChoScreen
 *     /app/trending            → TrendingScreen
 *     /app/tags/:slug          → HashtagDetailScreen
 *     /app/posts/:id           → PostDetailScreen
 *     /app/search              → SearchScreen
 *     /app/notifications       → NotificationsScreen
 *     /app/profile/me          → MyProfileScreen
 *     /app/profile/me/edit     → EditProfileScreen (K3)
 *     /app/profile/:username   → PublicProfileScreen
 *     /app/publish             → PublishCategoryPicker
 *     /app/publish/news        → PublishNewsScreen
 *     /app/publish/good-plan   → PublishGoodPlanScreen
 *     /app/publish/job         → PublishJobScreen
 *     /app/publish/event       → PublishEventScreen
 *     /app/publish/music       → PublishMusicScreen
 *     /app/publish/misc        → PublishMiscScreen
 *   *                          → NotFoundScreen (catch-all)
 *
 * All screens are lazy-imported so the router creates a separate chunk for
 * each one — keeping initial load lean and enabling per-route code splitting.
 */
import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { AuthGuard } from '@/features/auth/AuthGuard';
import { GuestGuard } from '@/features/auth/GuestGuard';
import { FullPageSkeleton } from '@/shared/components/FullPageSkeleton';
import { publishRoutes } from '@/features/publish/routes';
import { settingsRoutes } from '@/features/settings/routes';

// ---------------------------------------------------------------------------
// Lazy-loaded screens — each becomes its own JS chunk at build time.
// ---------------------------------------------------------------------------

const LoginScreen = lazy(
  () => import('@/features/auth/screens/LoginScreen'),
);
const SignupScreen = lazy(
  () => import('@/features/auth/screens/SignupScreen'),
);
const ForgotPasswordScreen = lazy(
  () => import('@/features/auth/screens/ForgotPasswordScreen'),
);
const ResetCodeScreen = lazy(
  () => import('@/features/auth/screens/ResetCodeScreen'),
);
const NewPasswordScreen = lazy(
  () => import('@/features/auth/screens/NewPasswordScreen'),
);

const AppLayout = lazy(
  () => import('@/features/layout/AppLayout'),
);

const FeedScreen = lazy(
  () => import('@/features/feed/screens/FeedScreen'),
);
const PostDetailScreen = lazy(
  () => import('@/features/feed/screens/PostDetailScreen'),
);
const KozeChoScreen = lazy(
  () => import('@/features/feed/screens/KozeChoScreen'),
);
const TrendingScreen = lazy(
  () => import('@/features/feed/screens/TrendingScreen'),
);
const HashtagDetailScreen = lazy(
  () => import('@/features/tags/screens/HashtagDetailScreen'),
);

const SearchScreen = lazy(
  () => import('@/features/search/screens/SearchScreen'),
);

const NotificationsScreen = lazy(
  () => import('@/features/notifications/screens/NotificationsScreen'),
);

const MyProfileScreen = lazy(
  () => import('@/features/profile/screens/MyProfileScreen'),
);
const EditProfileScreen = lazy(
  () => import('@/features/profile/screens/EditProfileScreen'),
);
const PublicProfileScreen = lazy(
  () => import('@/features/profile/screens/PublicProfileScreen'),
);

const FollowersScreen = lazy(
  () => import('@/features/follows/screens/FollowersScreen'),
);
const FollowingsScreen = lazy(
  () => import('@/features/follows/screens/FollowingsScreen'),
);

const EditPostScreen = lazy(
  () => import('@/features/posts/pages/EditPostScreen'),
);

const NotFoundScreen = lazy(
  () => import('@/shared/screens/NotFoundScreen'),
);

// ---------------------------------------------------------------------------
// Thin wrapper — provides a <Suspense> boundary for each lazy screen so the
// FullPageSkeleton shows while the chunk is loading.
// ---------------------------------------------------------------------------

function LazyScreen({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<FullPageSkeleton />}>{children}</Suspense>;
}

// ---------------------------------------------------------------------------
// Route tree
// ---------------------------------------------------------------------------

export const router = createBrowserRouter([
  // Root redirect → app shell
  {
    path: '/',
    element: <Navigate to="/app" replace />,
  },

  // ── Guest-only routes (redirect to /app if already authenticated) ─────────
  {
    element: <GuestGuard />,
    children: [
      {
        path: '/login',
        element: (
          <LazyScreen>
            <LoginScreen />
          </LazyScreen>
        ),
      },
      {
        path: '/signup',
        element: (
          <LazyScreen>
            <SignupScreen />
          </LazyScreen>
        ),
      },
      {
        path: '/forgot-password',
        element: (
          <LazyScreen>
            <ForgotPasswordScreen />
          </LazyScreen>
        ),
      },
      {
        path: '/reset-code',
        element: (
          <LazyScreen>
            <ResetCodeScreen />
          </LazyScreen>
        ),
      },
      {
        path: '/new-password',
        element: (
          <LazyScreen>
            <NewPasswordScreen />
          </LazyScreen>
        ),
      },
    ],
  },

  // ── Authenticated routes (redirect to /login if no session) ──────────────
  {
    element: <AuthGuard />,
    children: [
      {
        path: '/app',
        element: (
          <LazyScreen>
            <AppLayout />
          </LazyScreen>
        ),
        children: [
          // Default: redirect /app → /app/ feed (no category filter)
          {
            index: true,
            element: (
              <LazyScreen>
                <FeedScreen />
              </LazyScreen>
            ),
          },
          // Category-filtered feed: /app/news, /app/bons-plans, /app/jobs, etc.
          // The :category slug is mapped to PostCategory by categoryFromRoute().
          {
            path: ':category',
            element: (
              <LazyScreen>
                <FeedScreen />
              </LazyScreen>
            ),
          },
          {
            path: 'kozecho',
            element: (
              <LazyScreen>
                <KozeChoScreen />
              </LazyScreen>
            ),
          },
          {
            path: 'trending',
            element: (
              <LazyScreen>
                <TrendingScreen />
              </LazyScreen>
            ),
          },
          {
            path: 'tags/:slug',
            element: (
              <LazyScreen>
                <HashtagDetailScreen />
              </LazyScreen>
            ),
          },
          {
            path: 'posts/:id',
            element: (
              <LazyScreen>
                <PostDetailScreen />
              </LazyScreen>
            ),
          },
          {
            path: 'posts/:id/edit',
            element: (
              <LazyScreen>
                <EditPostScreen />
              </LazyScreen>
            ),
          },
          {
            path: 'search',
            element: (
              <LazyScreen>
                <SearchScreen />
              </LazyScreen>
            ),
          },
          {
            path: 'notifications',
            element: (
              <LazyScreen>
                <NotificationsScreen />
              </LazyScreen>
            ),
          },
          {
            path: 'profile/me',
            element: (
              <LazyScreen>
                <MyProfileScreen />
              </LazyScreen>
            ),
          },
          {
            path: 'profile/me/edit',
            element: (
              <LazyScreen>
                <EditProfileScreen />
              </LazyScreen>
            ),
          },
          {
            path: 'profile/:username',
            element: (
              <LazyScreen>
                <PublicProfileScreen />
              </LazyScreen>
            ),
          },
          {
            path: 'me/followers',
            element: (
              <LazyScreen>
                <FollowersScreen />
              </LazyScreen>
            ),
          },
          {
            path: 'me/followings',
            element: (
              <LazyScreen>
                <FollowingsScreen />
              </LazyScreen>
            ),
          },

          // ── Publish routes — lazy-loaded category publish screens ───────────
          // Each screen is independently code-split via lazy() in publish/routes.tsx.
          // Full implementation in Group D; these are stubs until then.
          ...publishRoutes,

          // ── Settings routes — account, security, notifs, privacy, etc. ──────
          ...settingsRoutes,
        ],
      },
    ],
  },

  // ── 404 catch-all ────────────────────────────────────────────────────────
  {
    path: '*',
    element: (
      <LazyScreen>
        <NotFoundScreen />
      </LazyScreen>
    ),
  },
]);

export default router;
